import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import './Billing.css';

// Cloud Function base URL
const STRIPE_API_BASE =
  'https://us-central1-clipit-8b9bd.cloudfunctions.net/stripeAPI';

// Price IDs for plan switching
const PRICE_IDS = {
  Silver: 'price_1ThJkR3VHf8WBCOl34IBGMjx',
  Gold: 'price_1ThJkp3VHf8WBCOlduBuxlCZ',
};

const PLAN_PRICES = {
  Silver: '$5.99',
  Gold: '$8.49',
};

const Billing = () => {
  const { user, loading: authLoading, getIdToken } = useAuth();
  const navigate = useNavigate();

  // Subscription state (from Firestore users/{uid} document)
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(true);

  // Modal States
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);

  // Action loading states
  const [actionLoading, setActionLoading] = useState(null);

  // Route protection
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Listen to user document from Firestore in real-time
  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const status = data.subscriptionStatus;

        if (status && ['active', 'trialing', 'past_due'].includes(status)) {
          setSubscription({
            id: snapshot.id,
            planName: data.tier || 'Unknown',
            status: data.subscriptionStatus,
            subscriptionPriceId: data.subscriptionPriceId,
            currentPeriodStart: data.currentPeriodStart,
            currentPeriodEnd: data.currentPeriodEnd,
            cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
          });
        } else {
          setSubscription(null);
        }
      } else {
        setSubscription(null);
      }
      setSubLoading(false);
    });

    return unsubscribe;
  }, [user]);

  if (authLoading || !user) return null;

  // ---------- API helpers ----------
  const callStripeAPI = async (endpoint, body = {}) => {
    const token = await getIdToken();
    const res = await fetch(`${STRIPE_API_BASE}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  // ---------- Actions ----------
  const handleCancelSubscription = async () => {
    setActionLoading('cancel');
    try {
      await callStripeAPI('/v1/subscription/cancel');
      setIsCancelModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      await callStripeAPI('/v1/subscription/reactivate');
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePlan = async (newPlanName) => {
    const newPriceId = PRICE_IDS[newPlanName];
    if (!newPriceId) return;

    setActionLoading('change');
    try {
      await callStripeAPI('/v1/subscription/change', { newPriceId });
      setIsChangePlanModalOpen(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleManagePayment = async () => {
    setActionLoading('portal');
    try {
      const data = await callStripeAPI('/v1/billing/portal');
      window.location.href = data.url;
    } catch (err) {
      alert(err.message);
      setActionLoading(null);
    }
  };

  // ---------- Derived values ----------
  const planName = subscription?.planName || 'No Plan';
  const planPrice = PLAN_PRICES[planName] || '—';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isCanceling = subscription?.cancelAtPeriodEnd === true;

  const nextBillingDate = subscription?.currentPeriodEnd
    ? (subscription.currentPeriodEnd.toDate
        ? subscription.currentPeriodEnd.toDate()
        : new Date(subscription.currentPeriodEnd)
      ).toLocaleDateString()
    : '—';

  const statusLabel = isCanceling
    ? 'Cancels ' + nextBillingDate
    : subscription?.status === 'past_due'
    ? 'Past Due'
    : isActive
    ? 'Active'
    : 'Inactive';

  const statusClass = isCanceling
    ? 'canceling'
    : subscription?.status === 'past_due'
    ? 'past-due'
    : isActive
    ? 'active'
    : 'inactive';

  // ---------- No subscription state ----------
  if (!subLoading && !subscription) {
    return (
      <div className="billing-container">
        <div className="billing-header">
          <h1 className="billing-title">Kaylus Billing</h1>
          <p className="billing-subtitle">You don't have an active subscription yet.</p>
        </div>
        <div className="billing-grid">
          <div className="billing-card" style={{ textAlign: 'center' }}>
            <h2>Get Started</h2>
            <p style={{ margin: '1rem 0', color: 'var(--text-secondary, #6b7280)' }}>
              Choose a plan to unlock Kaylus features.
            </p>
            <button className="action-button primary" onClick={() => navigate('/pricing')}>
              View Plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-container">
      <div className="billing-header">
        <h1 className="billing-title">Kaylus Billing</h1>
        <p className="billing-subtitle">Manage your Kaylus plan, billing details, and invoices.</p>
      </div>

      {subLoading ? (
        <div className="billing-grid">
          <div className="billing-card">
            <p style={{ textAlign: 'center', color: '#6b7280' }}>Loading subscription…</p>
          </div>
        </div>
      ) : (
        <>
          <div className="billing-grid">
            {/* CURRENT PLAN CARD */}
            <div className="billing-card">
              <div className="card-top">
                <h2>Current Plan</h2>
                <span className={`status-badge ${statusClass}`}>{statusLabel}</span>
              </div>
              <div className="plan-details">
                <h3 className="plan-name">{planName}</h3>
                <p className="plan-price">
                  {planPrice} <span className="period">/ month</span>
                </p>
                <p className="plan-renewal">
                  {isCanceling ? 'Access until: ' : 'Next billing date: '}
                  <strong>{nextBillingDate}</strong>
                </p>
              </div>
              {!isCanceling && (
                <button
                  className="action-button primary"
                  onClick={() => setIsChangePlanModalOpen(true)}
                >
                  Change Plan
                </button>
              )}
              {isCanceling && (
                <button
                  className="action-button primary"
                  onClick={handleReactivate}
                  disabled={actionLoading === 'reactivate'}
                >
                  {actionLoading === 'reactivate' ? 'Reactivating…' : 'Reactivate Subscription'}
                </button>
              )}
            </div>

            {/* PAYMENT METHOD CARD */}
            <div className="billing-card">
              <div className="card-top">
                <h2>Payment & Invoices</h2>
              </div>
              <div className="payment-details">
                <p style={{ color: '#6b7280', margin: '0.5rem 0 1rem' }}>
                  Manage your payment methods, view invoices, and update billing information through
                  the secure Stripe portal.
                </p>
              </div>
              <button
                className="action-button secondary"
                onClick={handleManagePayment}
                disabled={actionLoading === 'portal'}
              >
                {actionLoading === 'portal' ? 'Opening…' : 'Manage Payment & Invoices'}
              </button>
            </div>
          </div>

          {/* DANGER ZONE */}
          {!isCanceling && (
            <div className="danger-zone">
              <button
                className="cancel-button"
                onClick={() => setIsCancelModalOpen(true)}
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </>
      )}

      {/* MODALS */}

      {/* Cancel Modal */}
      {isCancelModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Cancel Subscription</h3>
            <p>
              Are you sure you want to cancel? You will lose access to {planName} features at the
              end of your current billing cycle ({nextBillingDate}).
            </p>
            <div className="modal-actions">
              <button
                className="action-button secondary"
                onClick={() => setIsCancelModalOpen(false)}
                disabled={actionLoading === 'cancel'}
              >
                Keep Subscription
              </button>
              <button
                className="action-button danger"
                onClick={handleCancelSubscription}
                disabled={actionLoading === 'cancel'}
              >
                {actionLoading === 'cancel' ? 'Cancelling…' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {isChangePlanModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <h3>Change Your Plan</h3>
            <p>Select a new tier below. We will automatically prorate the difference.</p>
            <div className="plan-options">
              {Object.entries(PRICE_IDS).map(([name]) => {
                const isCurrent = name === planName;
                return (
                  <div
                    key={name}
                    className={`plan-option ${isCurrent ? 'current' : ''}`}
                    onClick={() => !isCurrent && handleChangePlan(name)}
                    style={{ cursor: isCurrent ? 'default' : 'pointer' }}
                  >
                    <h4>{name}</h4>
                    <p>{isCurrent ? 'Current Plan' : `${PLAN_PRICES[name]} / month`}</p>
                  </div>
                );
              })}
            </div>
            <button
              className="cancel-link"
              onClick={() => setIsChangePlanModalOpen(false)}
              disabled={actionLoading === 'change'}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
